import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Services() {
  useEffect(() => {
    // Update page title and meta description
    document.title = 'AI služby pro firmy | 10 kategorií AI řešení | CzechAI';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '10 kategorií AI služeb pro český byznys. Automatizace procesů, chatboti, marketing, analytika a další AI řešení. Více než 80 různých AI nástrojů pro vaši firmu.');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI služby pro firmy",
      "description": "Kompletní portfolio AI služeb uspořádaných do 10 specializovaných kategorií",
      "provider": {
        "@type": "Organization",
        "name": "CzechAI",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com"
      },
      "serviceType": "Umělá inteligence a automatizace",
      "areaServed": {
        "@type": "Country",
        "name": "Česká republika"
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "AI pro obchod a zákazníky",
          "description": "Inteligentní řešení pro zlepšení vztahů se zákazníky a optimalizaci obchodních procesů"
        },
        {
          "@type": "Offer", 
          "name": "Automatizace procesů",
          "description": "Eliminujte rutinní úkoly a zefektivněte své podnikové procesy pomocí AI"
        },
        {
          "@type": "Offer",
          "name": "Chatboti & zákaznický servis", 
          "description": "Inteligentní chatboti pro dokonalý zákaznický zážitek 24/7"
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const [isDragging, setIsDragging] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [startDragPosition, setStartDragPosition] = useState({ x: 0, y: 0 });
  const [isPanMode, setIsPanMode] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isPanMode) return;
    
    setIsDragging(true);
    setStartDragPosition({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !isPanMode) return;
    
    setImagePosition({
      x: e.clientX - startDragPosition.x,
      y: e.clientY - startDragPosition.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetPosition = () => {
    setImagePosition({ x: 0, y: 0 });
  };

  const togglePanMode = () => {
    setIsPanMode(!isPanMode);
    if (isPanMode) {
      resetPosition();
    }
  };

  const serviceCategories = [
    {
      id: 'obchod-zakaznici',
      title: '1. AI pro obchod a zákazníky',
      icon: 'ri-customer-service-2-line',
      description: 'Inteligentní řešení pro zlepšení vztahů se zákazníky a optimalizaci obchodních procesů',
      services: [
        'AI Obchodní zástupce',
        'AI Customer Support', 
        'AI Recepční / Asistentka',
        'AI Personalista (HR)',
        'AI Social Media Manager',
        'AI Copywriter & SEO',
        'AI Právní asistent',
        'AI Finanční analytik',
        'AI Sales Coach',
        'AI Voice-to-CRM'
      ]
    },
    {
      id: 'automatizace-procesu',
      title: '2. Automatizace procesů',
      icon: 'ri-robot-2-line',
      description: 'Eliminujte rutinní úkoly a zefektivněte své podnikové procesy pomocí AI',
      services: [
        'Automatické zpracování faktur',
        'Automatizace skladu & logistiky',
        'Automatické reporty (KPI)',
        'AI správa dokumentů',
        'Automatizace onboardingu',
        'AI Meeting Assistant',
        'AI Contract Management',
        'AI Expense Control'
      ]
    },
    {
      id: 'chatboti-servis',
      title: '3. Chatboti & zákaznický servis',
      icon: 'ri-chat-bot-line',
      description: 'Inteligentní chatboti pro dokonalý zákaznický zážitek 24/7',
      services: [
        'Hyper-personalizovaný nákupní asistent',
        'AI Hlasový asistent',
        'Lead-gen chatbot',
        'Analytický chatbot',
        'Proaktivní servisní chatbot',
        'Multijazyčný AI chatbot'
      ]
    },
    {
      id: 'marketing-prodej',
      title: '4. Marketing & prodej',
      icon: 'ri-rocket-2-line',
      description: 'AI nástroje pro personalizovaný marketing a optimalizaci prodeje',
      services: [
        'Prediktivní analýza zákazníků',
        'Dynamická cenotvorba',
        'Personalizace webu & e-mailů',
        'AI pro influencer marketing',
        'Generování vizuálů & videí',
        'AI Ad Optimizer',
        'Generátor landing pages'
      ]
    },
    {
      id: 'skoleni-rozvoj',
      title: '5. Školení & rozvoj',
      icon: 'ri-graduation-cap-line',
      description: 'Personalizované vzdělávání a rozvoj zaměstnanců pomocí AI',
      services: [
        'Personalizované vzdělávání',
        'AI Simulátor rozhovorů',
        'AI Jazykový kouč',
        'AI školení compliance/BOZP',
        'AI Leadership Mentor',
        'Microlearning platforma'
      ]
    },
    {
      id: 'analytika-bi',
      title: '6. Analytika & BI',
      icon: 'ri-bar-chart-line',
      description: 'Transformujte vaše data na užitečné poznatky a predikce',
      services: [
        'Analýza sentimentu',
        'Predikce fluktuace zaměstnanců',
        'Konkurenční analýza',
        'Optimalizace výroby',
        'AI Cashflow Predictor',
        'Sustainability Analytics'
      ]
    },
    {
      id: 'odborne-sluzby',
      title: '7. Odborné a důvěrné služby',
      icon: 'ri-shield-check-line',
      description: 'Specializované AI řešení pro strategické a rizikové analýzy',
      services: [
        'AI Due Diligence',
        'Generativní AI strategie',
        'AI Risk Scoring',
        'Scenario Planning AI'
      ]
    },
    {
      id: 'research-analyzy',
      title: '8. Research & analýzy',
      icon: 'ri-search-2-line',
      description: 'Komplexní výzkum a analýzy trhu, konkurence a příležitostí',
      services: [
        'Due diligence firem',
        'Customer persona research',
        'Tender monitoring',
        'Investment screening',
        'Compliance monitoring',
        'Brand sentiment analysis',
        'Konkurenční monitoring',
        'Lead generation',
        'Market research',
        'Supplier scouting',
        'Talent Market Analysis',
        'Technology Radar'
      ]
    },
    {
      id: 'content-marketing',
      title: '9. Content & marketing',
      icon: 'ri-article-line',
      description: 'AI generování obsahu pro všechny marketingové potřeby',
      services: [
        'SEO články a blogy',
        'Plánování sociálních sítí',
        'E-mail marketing kampaně',
        'Whitepapers a case studies',
        'Prezentace a pitch decky',
        'Produktové popisy',
        'Tiskové zprávy',
        'LinkedIn branding',
        'Videoscénáře',
        'Newslettery',
        'AI Design Assistant',
        'Podcast & Audio generátor'
      ]
    },
    {
      id: 'automatizace-monitoring',
      title: '10. Automatizace & monitoring',
      icon: 'ri-dashboard-line',
      description: 'Komplexní automatizace a monitoring všech klíčových procesů ve firmě',
      services: [
        'Automatizace opakujících se úkolů',
        'Chatboty pro zákaznickou podporu',
        'Personalizace marketingových kampaní',
        'Školení zaměstnanců pomocí AI modulů',
        'Monitoring procesů a KPI',
        'Prediktivní monitoring',
        'Real estate portfolio analýza',
        'Optimalizace zásob',
        'Sales forecasting',
        'Facility Management AI',
        'AI Security Monitoring'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero sekce s obrázkem a možností posunování */}
      <section className="relative">
        {/* Hlavní obrázek s možností posunování */}
        <div className="relative overflow-hidden min-h-[60vh] md:min-h-[70vh]">
          <div 
            className={`relative ${isPanMode ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img 
              src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/6e50874ea13fa1a3bca382aab10de581.jfif"
              alt="AI technologie"
              className="w-full h-full min-h-[60vh] md:min-h-[70vh] object-cover transition-transform duration-100"
              style={{
                transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`
              }}
              draggable={false}
            />
            
            {/* Overlay s obsahem */}
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center px-4">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
                  AI řešení pro každou oblast vašeho podnikání
                </h1>
                <p className="text-lg sm:text-xl text-gray-300 mb-6 md:mb-8 max-w-4xl mx-auto leading-relaxed">
                  Kompletní portfolio AI služeb uspořádaných do 10 specializovaných kategorií. 
                  Vyberte si z více než 80 různých AI nástrojů a řešení.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
                  <Link to="/kontakt" className="w-full sm:w-auto">
                    <Button size="lg" className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-black w-full sm:w-auto">
                      <i className="ri-file-text-line mr-2"></i>
                      Vyplnit formulář
                    </Button>
                  </Link>
                  <Link to="/sluzby-detailne" className="w-full sm:w-auto">
                    <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black w-full sm:w-auto">
                      <i className="ri-information-line mr-2"></i>
                      Detailní informace
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Ovládací panel pro posunování - skrytý na mobilech */}
          <div className="absolute top-4 right-4 hidden md:flex flex-col gap-2">
            <button
              onClick={togglePanMode}
              className={`px-3 py-2 text-sm rounded-lg text-white font-medium transition-colors cursor-pointer ${
                isPanMode 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              <i className={`ri-${isPanMode ? 'close' : 'drag-move'}-line mr-1`}></i>
              {isPanMode ? 'Zrušit' : 'Posunovat'}
            </button>
            
            {isPanMode && (
              <button
                onClick={resetPosition}
                className="px-3 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line mr-1"></i>
                Reset
              </button>
            )}
          </div>

          {/* Návod k použití - jen na desktopu */}
          {isPanMode && (
            <div className="absolute bottom-4 left-4 bg-black/80 text-white px-3 py-2 rounded-lg hidden md:block">
              <p className="text-sm">
                <i className="ri-information-line mr-2"></i>
                Přetáhněte obrázek myší pro změnu pozice
              </p>
            </div>
          )}

          {/* Zobrazení aktuální pozice - jen na desktopu */}
          {isPanMode && (imagePosition.x !== 0 || imagePosition.y !== 0) && (
            <div className="absolute top-4 left-4 bg-black/80 text-white px-3 py-2 rounded-lg hidden md:block">
              <p className="text-xs">
                Pozice: X: {imagePosition.x}px, Y: {imagePosition.y}px
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Kategorie služeb */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              10 kategorií AI řešení
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Každá kategorie obsahuje specializované nástroje a služby přizpůsobené konkrétním potřebám
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
            {serviceCategories.map((category, index) => (
              <div key={index} id={category.id} className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 cursor-pointer group">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-black transition-colors">
                  <i className={`${category.icon} text-xl md:text-2xl text-gray-700 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight">{category.title}</h3>
                <p className="text-gray-600 mb-4 md:mb-6 text-sm leading-relaxed">{category.description}</p>
                
                <div className="mb-4 md:mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Služby ({category.services.length}):</h4>
                  <ul className="space-y-2">
                    {category.services.map((service, serviceIndex) => (
                      <li key={serviceIndex} className="flex items-start text-sm text-gray-600">
                        <i className="ri-check-line text-black mr-2 text-xs mt-0.5 flex-shrink-0"></i>
                        <span className="leading-relaxed">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="pt-4 md:pt-6 border-t border-gray-100">
                  <button
                    onClick={() => {
                      // Navigate to detail page and scroll to specific category
                      window.location.href = `/sluzby-detailne#${category.id}`;
                    }}
                    className="w-full"
                  >
                    <Button size="sm" variant="outline" className="w-full text-sm">
                      Více informací
                      <i className="ri-arrow-right-line ml-1"></i>
                    </Button>
                  </button>
                </div>
              </div>
            ))}
            
            {/* Tech obrázek - na mobilu celá šířka, na desktopu přes dvě kategorie */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 md:p-8 flex items-center justify-center md:col-span-2 xl:col-span-2">
              <img
                src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/0c6336d6f4d63f5f289967c145a8efa1.jfif"
                alt="AI Technologie"
                className="w-full h-48 md:h-full object-cover object-top rounded-xl opacity-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Statistiky */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Naše AI portfolio v číslech
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-black mb-2">10</div>
              <div className="text-sm md:text-base text-gray-600">Kategorií služeb</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-black mb-2">80+</div>
              <div className="text-sm md:text-base text-gray-600">Různých AI nástrojů</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-black mb-2">150+</div>
              <div className="text-sm md:text-base text-gray-600">Spokojených klientů</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-black mb-2">95%</div>
              <div className="text-sm md:text-base text-gray-600">Míra úspěšnosti</div>
            </div>
          </div>
        </div>
      </section>

      {/* Jak probíhá implementace */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Jak probíhá implementace?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Transparentní proces od analýzy po nasazení a dlouhodobou podporu
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-black text-white rounded-full flex items-center justify-center text-lg md:text-xl font-bold mx-auto mb-4">
                01
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Analýza potřeb</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Provedeme důkladnou analýzu vašich procesů a identifikujeme oblasti pro zlepšení
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-black text-white rounded-full flex items-center justify-center text-lg md:text-xl font-bold mx-auto mb-4">
                02
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Návrh řešení</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Připravíme detailní návrh AI řešení včetně ROI analýzy a harmonogramu
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-black text-white rounded-full flex items-center justify-center text-lg md:text-xl font-bold mx-auto mb-4">
                03
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Implementace</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Postupně nasadíme řešení s minimálním dopadem na vaše běžné procesy
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-black text-white rounded-full flex items-center justify-center text-lg md:text-xl font-bold mx-auto mb-4">
                04
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Podpora</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Poskytujeme školení, údržbu a kontinuální optimalizaci systému
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            Začněte už dnes
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-6 md:mb-8">
            Získejte nezávaznou konzultaci a zjistěte, jak vám AI může pomoci
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
            <Link to="/kontakt" className="w-full sm:w-auto">
              <Button size="lg" className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-black w-full sm:w-auto">
                <i className="ri-file-text-line mr-2"></i>
                Vyplnit formulář
              </Button>
            </Link>
            <Link to="/sluzby-detailne" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black w-full sm:w-auto">
                <i className="ri-eye-line mr-2"></i>
                Detailní služby
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
